import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import {
  BrandAssetLibrary,
  BrandAssetUploadFolder,
} from '../molecules/creatives/creatives.types';

export interface BrandAssetUploadUrlResponse {
  url: string;
  key: string;
  publicUrl: string;
  bucket: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminBrandingCreativesService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.shout_api.url;

  getCreativesLibrary(): Observable<BrandAssetLibrary> {
    return this.http.get<BrandAssetLibrary>(`${this.api}/branding/creatives`);
  }

  getUploadUrl(body: {
    folder: BrandAssetUploadFolder;
    fileName: string;
    contentType: string;
  }): Observable<BrandAssetUploadUrlResponse> {
    return this.http.post<BrandAssetUploadUrlResponse>(
      `${this.api}/branding/creatives/upload-url`,
      body,
    );
  }

  async uploadCreative(
    folder: BrandAssetUploadFolder,
    file: File,
  ): Promise<BrandAssetUploadUrlResponse> {
    const contentType =
      file.type && (file.type.startsWith('image/') || file.type.startsWith('video/'))
        ? file.type
        : this.contentTypeFromFileName(file.name);

    const signed = await firstValueFrom(
      this.getUploadUrl({
        folder,
        fileName: file.name,
        contentType,
      }),
    );

    try {
      await firstValueFrom(
        this.http.put(signed.url, file, {
          headers: {
            'Content-Type': contentType,
          },
        }),
      );
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      if (!status) {
        throw {
          error: {
            message:
              'Browser could not PUT to S3 (often missing bucket CORS or PutObject IAM).',
          },
        };
      }
      throw err;
    }

    return signed;
  }

  async replaceCreative(key: string, file: File): Promise<BrandAssetUploadUrlResponse> {
    const existingName = key.split('/').pop() ?? file.name;
    const contentType = this.contentTypeFromFileName(existingName);

    const signed = await firstValueFrom(
      this.http.post<BrandAssetUploadUrlResponse>(`${this.api}/branding/creatives/replace-url`, {
        key,
        contentType,
      }),
    );

    try {
      await firstValueFrom(
        this.http.put(signed.url, file, {
          headers: {
            'Content-Type': contentType,
          },
        }),
      );
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      if (!status) {
        throw {
          error: {
            message:
              'Browser could not PUT to S3 (often missing bucket CORS or PutObject IAM).',
          },
        };
      }
      throw err;
    }

    return signed;
  }

  async deleteCreative(key: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.api}/branding/creatives`, {
        body: { key },
      }),
    );
  }

  private contentTypeFromFileName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    const map: Record<string, string> = {
      svg: 'image/svg+xml',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      webp: 'image/webp',
      gif: 'image/gif',
      mp4: 'video/mp4',
      webm: 'video/webm',
      mov: 'video/quicktime',
      m4v: 'video/mp4',
    };
    return map[ext] || 'application/octet-stream';
  }
}
